import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { OrderBumpService } from './order-bump.service';
import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { OrderformFascade } from '../../fascades/orderform.fascade';
import { isPageBuilderDesignMode } from '../../misc/pagebuilder';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

/**
 * Diese Komponente wird nur noch verwendet, um Fehler
 * im OrderBump anzuzeigen!
 */
@Component({
  selector: 'orderform-order-bump',
  templateUrl: './order-bump.component.html',
  styleUrls: ['./order-bump.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderBumpComponent implements OnInit, OnDestroy {
  isEditor = isPageBuilderDesignMode();

  public componentId$ = new ReplaySubject<string | null>(1);
  /**
   * Orderbump Product
   */
  public productId$ = new ReplaySubject<string | null>(1);
  public content$ = new ReplaySubject<string | null>();
  public componentChange$ = this.content$.pipe(distinctUntilChanged());
  public hasInputWidget$ = new ReplaySubject<boolean>(1);

  private _destroy$ = new Subject<void>();

  @Input() set componentId(compId: string) {
    this.componentId$.next(compId);
  }

  @Input()
  private set productId(productId) {
    this.productId$.next(productId);
  }

  @Input() set content(content: string) {
    this.content$.next(content);
  }

  @Input() set hasInputWidget(value: any) {
    this.hasInputWidget$.next(coerceBooleanProperty(value));
  }

  contentValidWithInput$: Observable<boolean> = combineLatest([
    this.componentChange$,
    this.hasInputWidget$,
  ]).pipe(
    map(([content, hasInputWidget]) => {
      return (
        hasInputWidget ||
        (content.includes('ds-orderform-order-bump-placeholder') &&
          (content.includes('field="INPUT"') ||
            content.includes("field='INPUT'")))
      );
    })
  );

  mainProductId$ = this._orderFormFascade.config$.pipe(
    map((config) => config.product_id)
  );

  productIsMainProduct$ = combineLatest([
    this.mainProductId$,
    this.productId$,
  ]).pipe(
    map(([mainProductId, productId]) => {
      return '' + mainProductId === '' + productId;
    }),
    distinctUntilChanged(),
    shareReplay(1)
  );

  /**
   * Check is Orderbump Product exists in Card
   */
  public productMatch$: Observable<boolean> = this.productId$.pipe(
    switchMap((productId) => {
      return this._orderBumpService.getProductById(productId).pipe(
        map((product) => {
          return product && +productId === +product.product_id;
        })
      );
    }),
    shareReplay(1) // important
  );

  constructor(
    private _orderFormFascade: OrderformFascade,
    private _orderBumpService: OrderBumpService
  ) {
    combineLatest([
      this.componentId$,
      this.productMatch$,
      this.productIsMainProduct$,
    ])
      .pipe(
        takeUntil(this._destroy$),
        filter(() => !this.isEditor) // not in Editor
      )
      .subscribe(([compId, match, isMainProduct]) => {
        // has input placeholder widget
        const hasInputPlaceholderWidget =
          document
            .getElementById(compId)
            .querySelectorAll(
              'ds-orderform-order-bump-placeholder[field="INPUT"]'
            ).length > 0;
        if (match && hasInputPlaceholderWidget && !isMainProduct) {
          document.getElementById(compId).classList.add('d-block');
          document.getElementById(compId).classList.remove('d-none');
        } else {
          document.getElementById(compId).classList.add('d-none');
          document.getElementById(compId).classList.remove('d-block');
        }
      });
  }

  ngOnInit() {}

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
